/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { X, Info } from 'lucide-react';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { ChipSelectSearch } from '../../components/muiCustomized/ChipSelectSearchable';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../utils/styledSelect';
import SwitchToggle from './SwitchToggle';

const TABS = ['General', 'Members', 'Teams'];

function AddProjectModal({
  open, onClose, onSubmit, loading = false, title = 'New Project', initialData = {},
}) {
  const [activeTab, setActiveTab] = useState(0);

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  const [workspaceTeams, setWorkspaceTeams] = useState([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);

  const [formData, setFormData] = useState({
    name: initialData.name || '',
    description: initialData.description || '',
    billable: initialData.billable || true,
    disableActivity: initialData.disableActivity || false,
    disableIdleTime: initialData.disableIdleTime || false,
    client: initialData.client || '',
    managers: initialData.managers || [],
    members: initialData.members || [],
    viewers: initialData.viewers || [],
    teams: initialData.teams || [],
  });

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleChipChange = (e) => setField(e.target.name, e.target.value);

  const fetchMembers = async () => {
    setIsLoadingMembers(true);
    try {
      const { success, data } = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.members(activeWorkspace.id),
        params: {
          pageSize: 0,
        },
      });

      if (success) {
        const filteredMembers = data.members
          .filter(({ haveJoined }) => haveJoined)
          .map((item) => ({
            id: item.id,
            name: `${item.user.firstName} ${item.user.lastName}`,
          }));
        filteredMembers.sort((item1, item2) => {
          if (item1.name > item2.name) return 1;
          if (item1.name < item2.name) return -1;
          return 0;
        });
        setWorkspaceMembers(filteredMembers);
      } else {
        displayNotification({
          message: 'There was an issue fetching members in this workspace. Please try again',
          severity: 'error',
        });
      }
    } catch (error) {
      displayNotification({
        message: 'Error fetching workspace members',
        severity: 'error',
      });
    }
    setIsLoadingMembers(false);
  };

  const fetchTeams = async () => {
    setIsLoadingTeams(true);
    try {
      const { success, data } = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.getWorkspaceTeams(activeWorkspace.id),
        params: {
          pageSize: 0,
        },
      });

      if (success) {
        const filteredTeams = data.formattedTeams
          .map((item) => ({
            id: item.id,
            name: item.name,
          }));
        filteredTeams.sort((item1, item2) => {
          if (item1.name > item2.name) return 1;
          if (item1.name < item2.name) return -1;
          return 0;
        });
        setWorkspaceTeams(filteredTeams);
      } else {
        displayNotification({
          message: 'There was an issue fetching teams in this workspace. Please try again',
          severity: 'error',
        });
      }
    } catch (error) {
      displayNotification({
        message: 'Error fetching workspace teams',
        severity: 'error',
      });
    }
    setIsLoadingTeams(false);
  };

  const handleSelectAllTeams = () => {
    setFormData({
      ...formData,
      teams: [...workspaceTeams],
    });
  };

  const handleSelectAllManagers = () => {
    setFormData({
      ...formData,
      managers: workspaceMembers.filter((member) => !formData.members.some((m) => m.id === member.id)
        && !formData.viewers.some((v) => v.id === member.id)),
    });
  };

  const handleSelectAllMembers = () => {
    setFormData({
      ...formData,
      members: workspaceMembers.filter((member) => !formData.managers.some((m) => m.id === member.id)
        && !formData.viewers.some((v) => v.id === member.id)),
    });
  };

  const handleSelectAllViewers = () => {
    setFormData({
      ...formData,
      viewers: workspaceMembers.filter((member) => !formData.managers.some((m) => m.id === member.id)
        && !formData.members.some((m) => m.id === member.id)),
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = 'Project name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Project name must be at least 3 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      billable: true,
      disableActivity: false,
      disableIdleTime: false,
      client: '',
      managers: [],
      members: [],
      viewers: [],
      teams: [],
    });
    setErrors({});
    setSubmitError('');
    setActiveTab(0);
    onClose();
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitError('');

    try {
      const projectData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        billable: formData.billable,
        activity: !formData.disableActivity,
        idleTime: !formData.disableIdleTime,
        client: formData.client,
        members: {
          managers: formData.managers,
          members: formData.members,
          viewers: formData.viewers,
        },
        teams: formData.teams,
      };

      console.log('Sending project data:', projectData);

      await onSubmit(projectData);

      displayNotification({
        message: 'Project created successfully!',
        severity: 'success',
      });

      handleClose();
    } catch (error) {
      console.error('Error creating project:', error);
      setSubmitError(error.message || 'Failed to save project');
    }
  };

  useEffect(() => {
    if (open && activeWorkspace?.id) {
      fetchMembers();
      fetchTeams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeWorkspace?.id]);

  if (!open) return null;

  const managersAvailable = workspaceMembers.filter((member) => !formData.members.some((m) => m.id === member.id)
    && !formData.viewers.some((v) => v.id === member.id));
  const membersAvailable = workspaceMembers.filter((member) => !formData.managers.some((m) => m.id === member.id)
    && !formData.viewers.some((v) => v.id === member.id));
  const viewersAvailable = workspaceMembers.filter((member) => !formData.managers.some((m) => m.id === member.id)
    && !formData.members.some((m) => m.id === member.id));

  const inputCls = `w-full box-border h-10 px-3 border rounded-[8px] text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none ${errors.name ? 'border-[#CC4525]' : 'border-rule focus:border-brand-500'}`;

  const renderGeneralTab = () => (
    <div className="flex flex-col gap-4">
      {submitError && (
        <div className="px-3 py-2 rounded-[8px] text-sm text-[#CC4525] bg-[#fdf3f1] border border-[#f3cfc6]" style={{ borderStyle: 'solid' }}>
          {submitError}
        </div>
      )}

      <div>
        <span className="block text-sm font-medium text-ink-900 mb-1.5">Project Name</span>
        <input
          type="text"
          placeholder="Required"
          value={formData.name}
          onChange={(e) => setField('name', e.target.value)}
          disabled={loading}
          className={inputCls}
          style={{ borderStyle: 'solid' }}
        />
        {errors.name ? <p className="text-xs text-[#CC4525] mt-1.5">{errors.name}</p> : null}
      </div>

      <SwitchToggle label="Billable" checked={formData.billable} onChange={(v) => setField('billable', v)} disabled={loading} />
      <SwitchToggle label="Disable Activity" checked={formData.disableActivity} onChange={(v) => setField('disableActivity', v)} disabled={loading} />
      <SwitchToggle label="Disable Idle Time" checked={formData.disableIdleTime} onChange={(v) => setField('disableIdleTime', v)} disabled={loading} />

      <div>
        <span className="block text-sm font-medium text-ink-900 mb-1.5">Client</span>
        <select
          value={formData.client}
          onChange={(e) => setField('client', e.target.value)}
          disabled={loading}
          className={`${STYLED_SELECT_CLASS} w-full`}
          style={STYLED_SELECT_STYLE}
        >
          <option value="">Optional</option>
          <option value="client1">Client 1</option>
          <option value="client2">Client 2</option>
        </select>
      </div>
    </div>
  );

  const renderMembersTab = () => (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-2 px-3 py-2.5 rounded-[8px]" style={{ background: '#f8f9ff', border: '1px solid #d6d9ff' }}>
        <Info size={16} className="text-brand-500 flex-shrink-0" style={{ marginTop: 1 }} />
        <p className="text-xs text-ink-700 leading-relaxed">
          Moving Project viewers to Users or Managers will move them from a free seat to a paid seat, and may increase the number of paid seats on your plan.
        </p>
      </div>

      {isLoadingMembers ? (
        <p className="text-sm text-ink-500 text-center py-6">Loading workspace members…</p>
      ) : (
        <>
          <ChipSelectSearch redesign id="managers" label="Managers" placeholder="Search and select managers" selectAllBtn count={`${formData.managers.length}/${managersAvailable.length}`} selectedMembers={formData.managers} handleChange={handleChipChange} members={managersAvailable} handleSelectAll={handleSelectAllManagers} />
          <ChipSelectSearch redesign id="members" label="Members" placeholder="Search and select members" selectAllBtn count={`${formData.members.length}/${membersAvailable.length}`} selectedMembers={formData.members} handleChange={handleChipChange} members={membersAvailable} handleSelectAll={handleSelectAllMembers} />
          <ChipSelectSearch redesign id="viewers" label="Viewers" placeholder="Search and select viewers" selectAllBtn count={`${formData.viewers.length}/${viewersAvailable.length}`} selectedMembers={formData.viewers} handleChange={handleChipChange} members={viewersAvailable} handleSelectAll={handleSelectAllViewers} />
        </>
      )}
    </div>
  );

  const renderTeamsTab = () => (
    <div className="flex flex-col gap-4">
      {isLoadingTeams ? (
        <p className="text-sm text-ink-500 text-center py-6">Loading workspace teams…</p>
      ) : (
        <ChipSelectSearch redesign id="teams" label="Teams" placeholder="Search and select teams" selectAllBtn count={`${formData.teams.length}/${workspaceTeams.length}`} selectedMembers={formData.teams} handleChange={handleChipChange} members={workspaceTeams} handleSelectAll={handleSelectAllTeams} />
      )}
    </div>
  );

  return (
    <Modal open={open} onClose={handleClose} className="flex-row align-c jstfy-c">
      <Box style={{ width: 560, maxWidth: 'calc(100% - 32px)', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, fontFamily: 'Inter, sans-serif' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h5 className="text-lg font-semibold leading-none text-ink-900">{title}</h5>
          <button type="button" aria-label="Close" onClick={handleClose} className="text-ink-500 hover:text-ink-900 bg-transparent border-0 cursor-pointer p-0">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-6 border-b border-rule" style={{ borderBottomStyle: 'solid' }}>
          {TABS.map((label, i) => {
            const active = activeTab === i;
            return (
              <button
                key={label}
                type="button"
                onClick={() => setActiveTab(i)}
                className={`relative inline-flex items-center px-3 py-2.5 text-sm bg-transparent border-0 cursor-pointer transition ${active ? 'text-brand-700 font-semibold' : 'text-ink-500 font-medium hover:text-ink-900'}`}
              >
                {label}
                {active && <span className="absolute left-3 right-3 bottom-0" style={{ height: 2, background: '#315285', borderRadius: 2 }} />}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="px-6 py-5" style={{ minHeight: 280 }}>
          {activeTab === 0 && renderGeneralTab()}
          {activeTab === 1 && renderMembersTab()}
          {activeTab === 2 && renderTeamsTab()}
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center gap-2 px-6 pb-5 pt-1">
          <button type="button" onClick={handleClose} disabled={loading} className="inline-flex items-center justify-center box-border h-10 px-4 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}>Cancel</button>
          <button type="button" onClick={handleSubmit} disabled={loading || !formData.name.trim()} className="inline-flex items-center justify-center box-border h-10 px-4 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition">
            {loading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

AddProjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  title: PropTypes.string,
  initialData: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    billable: PropTypes.bool,
    disableActivity: PropTypes.bool,
    disableIdleTime: PropTypes.bool,
    client: PropTypes.string,
    managers: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    members: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    viewers: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    teams: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  }),
};

AddProjectModal.defaultProps = {
  loading: false,
  title: 'New Project',
  initialData: {},
};

export default AddProjectModal;
